import React, { useEffect, useState } from 'react';
import Hero from '../components/Hero';
import BlogCard from '../components/BlogCard';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import { blogAPI } from '../utils/api';

interface BlogPost {
  _id: string;
  title: string;
  excerpt: string;
  slug: string;
  categories: string[];
  createdAt: string;
  featuredImage?: string;
}

const HomePage: React.FC = () => {
  const [latestPosts, setLatestPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestPosts = async () => {
      try {
        setLoading(true);
        const response = await blogAPI.getPosts(1, 3, undefined, undefined, undefined, true);
        setLatestPosts(response.data.blogPosts);
      } catch (error) {
        console.error('Error fetching latest posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestPosts();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <Hero
        title="Rådgiver i hundeernæring"
        subtitle="Professionel rådgivning og vejledning til at sikre din hunds sundhed og velvære gennem optimal ernæring."
        ctaText="Book konsultation"
        ctaLink="/consultation"
        secondaryCtaText="Læs vores blog"
        secondaryCtaLink="/blog"
        backgroundImage="https://cdn.pixabay.com/photo/2017/07/31/21/15/dog-2561134_1280.jpg"
      />

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-center mb-12">Vores services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-snudefryd-light-purple rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-snudefryd-blue"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Personlig konsultation</h3>
              <p className="text-gray-600 mb-4">
                Få en skræddersyet ernæringsplan til din hund baseret på dens race, alder, vægt og sundhedstilstand.
              </p>
              <Link to="/consultation">
                <Button variant="outline" size="sm">
                  Læs mere
                </Button>
              </Link>
            </div>

            {/* Service 2 */}
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-snudefryd-light-purple rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-snudefryd-blue"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Blog</h3>
              <p className="text-gray-600 mb-4">
                Læs vores artikler om hundeernæring, sundhedstips og råd til at holde din hund sund og glad.
              </p>
              <Link to="/blog">
                <Button variant="outline" size="sm">
                  Læs mere
                </Button>
              </Link>
            </div>

            {/* Service 3 */}
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-snudefryd-light-purple rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-snudefryd-blue"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Ernæringsquiz</h3>
              <p className="text-gray-600 mb-4">
                Tag vores quiz og få personlige anbefalinger til din hunds ernæring baseret på dine svar.
              </p>
              <Link to="/quizzes">
                <Button variant="outline" size="sm">
                  Tag quizzen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts Section */}
      <section className="py-16">
        <div className="container-custom">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl font-bold">Seneste blogindlæg</h2>
            <Link to="/blog">
              <Button variant="outline">Se alle indlæg</Button>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-snudefryd-blue mx-auto"></div>
              <p className="mt-4 text-gray-600">Indlæser blogindlæg...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {latestPosts.map((post) => (
                <BlogCard
                  key={post._id}
                  id={post._id}
                  title={post.title}
                  slug={post.slug}
                  excerpt={post.excerpt}
                  featuredImage={post.featuredImage}
                  publishDate={post.createdAt}
                  categories={post.categories}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-snudefryd-blue text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">Klar til at forbedre din hunds ernæring?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Book en konsultation i dag og få professionel rådgivning skræddersyet til din hunds behov.
          </p>
          <Link to="/consultation">
            <Button variant="secondary" size="lg">
              Book konsultation nu
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 
